import React, { useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';
import {
  istErstattbar, vorgabeWeg, pruefeErstattung, euroText, ERSTATTUNG_WEGE, ERSTATTUNG_HINWEIS, ErstattungWeg, MIN_GRUND, MAX_REFERENZ,
} from '../../utils/erstattung';
import { ortsdatum } from '../../utils/kasse';

// T-180 (v1.8.57, Session 44, 05.10.2026): "Erstattung erfassen" in der Detailansicht einer bezahlten Rechnung (Kundin 05.10.2026:
// Stripe-Anzahlung erstattet, kein Knopf für die Stornorechnung). Vollerstattung; der Server storniert die Rechnung, erstellt die
// Stornorechnung und sendet sie auf Wunsch per Mail an den Gast. Muster ZahlungErfassenBereich (409 mit "Trotzdem erfassen").

const fehlertext = (err: any, rueckfall: string): string => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d) return d;
  return d?.message || d?.errorMessage || d?.error || err?.message || rueckfall;
};

export const ErstattungErfassenBereich: React.FC<{
  rechnung: {
    id: number; invoiceNumber: string; type: string; typeDisplayName?: string; status: string | number; amountPaid: number; adminNotes?: string;
  };
  onErfasst: (hinweis: string) => void;
}> = ({ rechnung, onErfasst }) => {
  const [offen, setOffen] = useState(false);
  const [datum, setDatum] = useState('');
  const [weg, setWeg] = useState<ErstattungWeg | ''>('');
  const [referenz, setReferenz] = useState('');
  const [grund, setGrund] = useState('');
  const [senden, setSenden] = useState(true);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const sperre = useRef(false);

  if (!istErstattbar(rechnung)) return null;

  const oeffnen = () => {
    setDatum(ortsdatum(new Date()));
    setWeg(vorgabeWeg(rechnung.adminNotes));
    setReferenz('');
    setGrund('');
    setSenden(true);
    setFehler(null);
    setHinweis(null);
    setOffen(true);
  };

  const erfassen = async (trotzdem: boolean) => {
    if (sperre.current) return;
    const meldung = pruefeErstattung(datum, ortsdatum(new Date()), weg, referenz, grund);
    if (meldung) {
      setFehler(meldung);
      return;
    }
    sperre.current = true;
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/invoices/${rechnung.id}/erstattung-erfassen`,
        {
          betrag: rechnung.amountPaid, datum, weg, referenz: referenz.trim() || null, grund: grund.trim(),
          stornorechnungSenden: senden, trotzdem,
        },
        { headers: getAuthHeaders() }
      );
      setOffen(false);
      setHinweis(null);
      onErfasst(r.data?.message || `Erstattung zu Rechnung ${rechnung.invoiceNumber} erfasst.`);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setHinweis(fehlertext(err, 'Die Buchung ist nicht storniert.'));
      } else {
        setFehler(fehlertext(err, 'Die Erstattung konnte nicht erfasst werden.'));
      }
    } finally {
      sperre.current = false;
      setLaeuft(false);
    }
  };

  return (
    <div className="detail-section" style={{ marginTop: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
      {!offen ? (
        <button className="btn-secondary" onClick={oeffnen} title="Nach einer Erstattung (Stripe, PayPal, Überweisung, bar): Rechnung stornieren, Stornorechnung erstellen">
          ↩️ Erstattung erfassen
        </button>
      ) : (
        <div>
          <h3>Erstattung erfassen, Rechnung {rechnung.invoiceNumber}</h3>
          <p className="section-hint">{ERSTATTUNG_HINWEIS}</p>
          <div className="form-group">
            <label>Betrag (Zahlungseingang, Vollerstattung)</label>
            <div className="form-input" style={{ background: '#f3f4f6' }}>{euroText(rechnung.amountPaid)}</div>
          </div>
          <div className="form-group">
            <label htmlFor="erWeg">Weg der Erstattung *</label>
            <select id="erWeg" className="form-input" value={weg} onChange={(e) => { setWeg(e.target.value as ErstattungWeg | ''); setHinweis(null); }} disabled={laeuft}>
              <option value="">Bitte wählen</option>
              {ERSTATTUNG_WEGE.map(w => <option key={w.wert} value={w.wert}>{w.text}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="erDatum">Datum der Erstattung *</label>
            <input id="erDatum" className="form-input" type="date" value={datum} max={ortsdatum(new Date())}
              onChange={(e) => { setDatum(e.target.value); setHinweis(null); }} disabled={laeuft} />
          </div>
          <div className="form-group">
            <label htmlFor="erReferenz">Referenz (optional, z. B. Stripe-Erstattung re_...)</label>
            <input id="erReferenz" className="form-input" type="text" maxLength={MAX_REFERENZ} value={referenz}
              onChange={(e) => { setReferenz(e.target.value); setHinweis(null); }} disabled={laeuft} />
          </div>
          <div className="form-group">
            <label htmlFor="erGrund">Grund (Pflicht, mindestens {MIN_GRUND} Zeichen)</label>
            <input id="erGrund" className="form-input" type="text" maxLength={300} value={grund}
              onChange={(e) => { setGrund(e.target.value); setHinweis(null); }} placeholder="z. B. Buchung storniert, Anzahlung erstattet" disabled={laeuft} />
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" checked={senden} onChange={(e) => setSenden(e.target.checked)} disabled={laeuft} />
              Stornorechnung per E-Mail an den Gast senden
            </label>
          </div>
          {fehler && <div className="error-message" style={{ whiteSpace: 'pre-line' }}>{fehler}</div>}
          {hinweis && (
            <div className="error-message" style={{ whiteSpace: 'pre-line' }}>
              {hinweis}
              <div style={{ marginTop: '0.5rem' }}>
                <button className="btn-secondary" onClick={() => erfassen(true)} disabled={laeuft}>
                  Trotzdem erfassen
                </button>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="btn-primary" onClick={() => erfassen(false)} disabled={laeuft || !weg || grund.trim().length < MIN_GRUND}>
              {laeuft ? 'Wird erfasst ...' : 'Erstattung erfassen'}
            </button>
            <button className="btn-secondary" onClick={() => { setOffen(false); setFehler(null); setHinweis(null); }} disabled={laeuft}>
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
